import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getMe } from '../features/authSlice';
import { useStateContext } from '../contexts/ContextProvider';
import {
  FaLock,
  FaSave,
  FaEye,
  FaEyeSlash,
  FaKey,
  FaCheckCircle,
  FaArrowLeft,
  FaLeaf,
  FaShieldAlt,
  FaExclamationTriangle,
} from 'react-icons/fa';

// ─────────────────────────────────────────────────────────────────────────
// PENTING: Komponen ini HARUS didefinisikan DI LUAR GantiPassword.
// Kalau didefinisikan di dalam, setiap re-render (misalnya tiap kali user
// mengetik satu huruf) akan membuat komponen ini dianggap "baru" oleh React,
// sehingga <input> di-unmount & mount ulang -> fokus hilang -> harus klik lagi.
// ─────────────────────────────────────────────────────────────────────────
const PasswordField = ({
  label,
  value,
  onChange,
  show,
  onToggle,
  error,
  placeholder,
  hint,
  accentColor,
  textPrimary,
  textSecondary,
  inputBg,
  inputBorder,
}) => (
  <div>
    <label className="block text-sm font-medium mb-2" style={{ color: textPrimary }}>
      {label} <span className="text-red-500">*</span>
    </label>
    <div className="relative">
      <FaLock
        className="absolute left-4 top-1/2 -translate-y-1/2 text-sm"
        style={{ color: error ? "#ef4444" : textSecondary }}
      />
      <input
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full px-4 py-3 pl-11 pr-12 rounded-lg border transition-all duration-300 outline-none"
        style={{
          backgroundColor: inputBg,
          borderColor: error ? "#ef4444" : inputBorder,
          color: textPrimary,
        }}
        onFocus={(e) => (e.target.style.borderColor = error ? "#ef4444" : accentColor)}
        onBlur={(e) => (e.target.style.borderColor = error ? "#ef4444" : inputBorder)}
      />
      <button
        type="button"
        onClick={onToggle}
        className="absolute right-4 top-1/2 -translate-y-1/2 transition-opacity duration-200 hover:opacity-70"
        style={{ color: textSecondary }}
      >
        {show ? <FaEyeSlash className="text-sm" /> : <FaEye className="text-sm" />}
      </button>
    </div>
    {error && (
      <p className="flex items-center gap-1.5 text-xs mt-1.5" style={{ color: "#ef4444" }}>
        <FaExclamationTriangle className="text-[10px]" /> {error}
      </p>
    )}
    {hint && !error && (
      <p className="text-xs mt-1.5" style={{ color: textSecondary }}>{hint}</p>
    )}
  </div>
);

const GantiPassword = () => {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confNewPassword, setConfNewPassword] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfPassword, setShowConfPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { currentColor, currentMode } = useStateContext();

  // ── Theme ────────────────────────────────────────────────────────────────
  const isDark = currentMode === "Dark";
  const accentColor = currentColor || "#c9862a";
  const bgColor = isDark ? "#1a1a18" : "#f7f3ec";
  const cardBg = isDark ? "#242420" : "#ffffff";
  const borderColor = isDark ? "#2e2e28" : "#e8e0d0";
  const textPrimary = isDark ? "#f0ece3" : "#1a1a18";
  const textSecondary = isDark ? "#a09880" : "#6b6456";
  const inputBg = isDark ? "#1a1a18" : "#ffffff";
  const inputBorder = isDark ? "#3a3a34" : "#e8e0d0";

  useEffect(() => { dispatch(getMe()); }, [dispatch]);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) navigate('/');
  }, [navigate, user]);

  // ── Password strength ────────────────────────────────────────────────────
  const getPasswordStrength = (pwd) => {
    if (!pwd) return null;
    if (pwd.length < 6) return { label: "Terlalu pendek", color: "#ef4444", width: "25%" };
    if (pwd.length < 8) return { label: "Lemah", color: "#f97316", width: "50%" };
    if (/[A-Z]/.test(pwd) && /[0-9]/.test(pwd)) return { label: "Kuat", color: "#22c55e", width: "100%" };
    return { label: "Cukup", color: "#eab308", width: "75%" };
  };
  const strength = getPasswordStrength(newPassword);

  // ── Validation ───────────────────────────────────────────────────────────
  const validateForm = () => {
    const newErrors = {};
    if (!oldPassword.trim()) newErrors.oldPassword = "Password lama wajib diisi";
    if (!newPassword.trim()) newErrors.newPassword = "Password baru wajib diisi";
    else if (newPassword.length < 6) newErrors.newPassword = "Password minimal 6 karakter";
    if (!confNewPassword.trim()) newErrors.confNewPassword = "Konfirmasi password wajib diisi";
    else if (newPassword !== confNewPassword) newErrors.confNewPassword = "Password tidak cocok";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage("");
    setErrors({});
    if (!validateForm()) return;

    try {
      setLoading(true);
      const token = localStorage.getItem("accessToken");
      const apiUrl = process.env.REACT_APP_URL_API;
      const response = await axios.patch(
        `${apiUrl}/change-password`,
        { oldPassword, newPassword, confNewPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOldPassword(""); setNewPassword(""); setConfNewPassword("");
      setSuccessMessage(response.data?.msg || "Password berhasil diubah");
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (error) {
      const errorMessage = error.response?.data?.msg || "Terjadi kesalahan";
      if (errorMessage.includes("Password lama")) setErrors({ oldPassword: errorMessage });
      else if (errorMessage.includes("tidak cocok")) setErrors({ confNewPassword: errorMessage });
      else setErrors({ general: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: bgColor }}>
      {/* Background deco */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 opacity-5">
          <FaShieldAlt className="absolute top-20 right-10 text-8xl" style={{ color: accentColor }} />
          <FaLeaf className="absolute bottom-20 left-20 text-7xl" style={{ color: accentColor }} />
        </div>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto">
        {/* ── Back button ── */}
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 mb-6 transition-all duration-300 hover:opacity-70"
          style={{ color: textSecondary }}
        >
          <FaArrowLeft className="text-sm" />
          <span className="text-sm font-medium">Kembali ke Dashboard</span>
        </button>

        {/* ── Header ── */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-6"
        >
          <div className="p-6 rounded-xl border" style={{ backgroundColor: cardBg, borderColor }}>
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl" style={{ backgroundColor: accentColor }}>
                <FaKey className="text-white text-xl" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-serif text-xs font-bold uppercase tracking-wider" style={{ color: textSecondary }}>
                    Keamanan Akun
                  </span>
                  <span className="text-xs" style={{ color: textSecondary }}>—</span>
                  <span className="font-serif text-xs font-bold" style={{ color: accentColor }}>
                    Rumoh Pangan Aceh
                  </span>
                </div>
                <h1 className="text-2xl font-serif font-bold" style={{ color: textPrimary }}>
                  Ganti Password
                </h1>
                <p className="text-sm mt-1" style={{ color: textSecondary }}>
                  Perbarui password akun Anda secara berkala untuk keamanan
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* ── Success Alert ── */}
        {successMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 flex items-center gap-3 px-5 py-4 rounded-xl border"
            style={{ backgroundColor: "#f0fdf4", borderColor: "#86efac" }}
          >
            <FaCheckCircle className="text-green-500 text-lg flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-green-700">{successMessage}</p>
              <p className="text-xs text-green-600 mt-0.5">Mengalihkan ke dashboard...</p>
            </div>
          </motion.div>
        )}

        {/* ── General Error Alert ── */}
        {errors.general && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 flex items-center gap-3 px-5 py-4 rounded-xl border"
            style={{ backgroundColor: "#fef2f2", borderColor: "#fca5a5" }}
          >
            <FaExclamationTriangle className="text-red-500 text-lg flex-shrink-0" />
            <p className="text-sm font-semibold text-red-700">{errors.general}</p>
          </motion.div>
        )}

        {/* ── Form ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
        >
          <form onSubmit={handleSubmit}>
            <div className="space-y-6">
              {/* ── Password Fields Card ── */}
              <div className="p-6 rounded-xl border" style={{ backgroundColor: cardBg, borderColor }}>
                <h3 className="font-semibold mb-6 flex items-center gap-2" style={{ color: textPrimary }}>
                  <FaShieldAlt className="text-sm" style={{ color: accentColor }} />
                  Ubah Password
                </h3>

                <div className="space-y-5">
                  {/* Old Password */}
                  <PasswordField
                    label="Password Lama"
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                    show={showOldPassword}
                    onToggle={() => setShowOldPassword((v) => !v)}
                    error={errors.oldPassword}
                    placeholder="Masukkan password lama"
                    accentColor={accentColor}
                    textPrimary={textPrimary}
                    textSecondary={textSecondary}
                    inputBg={inputBg}
                    inputBorder={inputBorder}
                  />

                  {/* Divider */}
                  <div className="flex items-center gap-3 py-1">
                    <div className="flex-1 h-px" style={{ backgroundColor: borderColor }} />
                    <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: textSecondary }}>
                      Password Baru
                    </span>
                    <div className="flex-1 h-px" style={{ backgroundColor: borderColor }} />
                  </div>

                  {/* New Password */}
                  <div>
                    <PasswordField
                      label="Password Baru"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      show={showNewPassword}
                      onToggle={() => setShowNewPassword((v) => !v)}
                      error={errors.newPassword}
                      placeholder="Masukkan password baru"
                      hint="Minimal 6 karakter"
                      accentColor={accentColor}
                      textPrimary={textPrimary}
                      textSecondary={textSecondary}
                      inputBg={inputBg}
                      inputBorder={inputBorder}
                    />
                    {/* Strength bar */}
                    {newPassword && !errors.newPassword && (
                      <div className="mt-3">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[11px]" style={{ color: textSecondary }}>Kekuatan password</span>
                          <span className="text-[11px] font-semibold" style={{ color: strength?.color }}>
                            {strength?.label}
                          </span>
                        </div>
                        <div className="w-full h-1.5 rounded-full" style={{ backgroundColor: `${accentColor}20` }}>
                          <div
                            className="h-full rounded-full transition-all duration-500"
                            style={{ width: strength?.width, backgroundColor: strength?.color }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Confirm Password */}
                  <PasswordField
                    label="Konfirmasi Password Baru"
                    value={confNewPassword}
                    onChange={(e) => setConfNewPassword(e.target.value)}
                    show={showConfPassword}
                    onToggle={() => setShowConfPassword((v) => !v)}
                    error={errors.confNewPassword}
                    placeholder="Ulangi password baru"
                    hint={
                      confNewPassword && newPassword === confNewPassword
                        ? "✓ Password cocok"
                        : undefined
                    }
                    accentColor={accentColor}
                    textPrimary={textPrimary}
                    textSecondary={textSecondary}
                    inputBg={inputBg}
                    inputBorder={inputBorder}
                  />
                </div>
              </div>

              {/* ── Action Buttons ── */}
              <div className="flex flex-col sm:flex-row gap-4 justify-end">
                <button
                  type="button"
                  onClick={() => navigate('/dashboard')}
                  disabled={loading}
                  className="flex-1 sm:flex-none px-6 py-3 rounded-xl font-medium transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: isDark ? "#1a1a18" : "#f7f3ec", color: textPrimary, border: `1px solid ${borderColor}` }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = isDark ? "#282C33" : "#e8e0d0")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = isDark ? "#1a1a18" : "#f7f3ec")}
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-8 py-3 rounded-xl font-medium text-white transition-all duration-300 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: accentColor }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#b5772a")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = accentColor)}
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Menyimpan...
                    </>
                  ) : (
                    <>
                      <FaSave className="text-sm" />
                      Simpan Password
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default GantiPassword;